"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { DestructiveTrashIcon } from "@/components/ui/DestructiveTrashIcon";
import React from "react";

type TrashButtonProps = {
  label: string;
  tooltipLabel?: string;
  onClick?: () => void | Promise<void>;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmLabel?: string;
  className?: string;
  iconClassName?: string;
  stopPropagation?: boolean;
};

export function TrashButton({
  label,
  tooltipLabel,
  onClick,
  confirmTitle,
  confirmDescription,
  confirmLabel,
  className,
  iconClassName,
  stopPropagation = false,
}: TrashButtonProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      cancel: "Zrušit",
      confirm: "Odstranit",
    },
    en: {
      cancel: "Cancel",
      confirm: "Delete",
    },
  }[language];
  const [isOpen, setIsOpen] = React.useState(false);
  const needsConfirmation = Boolean(confirmTitle || confirmDescription);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (needsConfirmation) {
      setIsOpen(true);
      return;
    }

    void onClick?.();
  };

  const button = (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="unstyled"
            aria-label={label}
            title={label}
            onClick={handleButtonClick}
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-full text-softiq-primary/40 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-white/40 dark:hover:bg-red-950/40 dark:hover:text-red-400",
              className,
            )}
          >
            <DestructiveTrashIcon
              aria-hidden="true"
              className={cn("size-4", iconClassName)}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={15}>
          {tooltipLabel ?? label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (!needsConfirmation) return button;

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      {button}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>{confirmDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{copy.cancel}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={() => void onClick?.()}
          >
            {confirmLabel ?? copy.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
